/* eslint-disable no-console */
/** @ts-check */
/**
 * Persists ad `src` query param in sessionStorage, sets cart attribute _ad_source.
 * Resolution: sessionStorage → URL `src` (then saved to session).
 * Clear rule: only remove _ad_source when session/url have no src AND cart has zero line items.
 * While the cart has items, missing session does not strip _ad_source.
 */
(function () {
  "use strict";

  /** @type {Window & { SHOPLC_AD_SRC_CART?: { sessionStorageKey?: string }; SHOPLC_UTM_CART?: { sessionStorageKey?: string; cookieName?: string } }} */
  const win = window;
  /** @type {{ sessionStorageKey?: string; cookieName?: string }} */
  const cfg = win.SHOPLC_AD_SRC_CART || win.SHOPLC_UTM_CART || {};
  const SESSION_KEY =
    cfg.sessionStorageKey || cfg.cookieName || "_shoplc_ad_src";
  const URL_PARAM = "src";
  const ATTR_KEY = "_ad_source";

  /** Same exclusions as cart-drawer.js; plus non-add cart flows */
  /** @type {Record<string, boolean>} */
  const IGNORE_SOURCES = {
    "cart-items-component": true,
    "cart-progress-bar": true,
    "bv-cart-progress": true,
    "secure-shield": true,
  };

  /**
   * @param {string} name
   * @returns {string}
   */
  const getParam = (name) => {
    try {
      const params = new URLSearchParams(win.location.search);
      const val = params.get(name);
      return typeof val === "string" ? val.trim() : "";
    } catch {
      return "";
    }
  };

  /**
   * @param {string} key
   * @returns {string}
   */
  const getSession = (key) => {
    try {
      const v = win.sessionStorage.getItem(key);
      return typeof v === "string" ? v.trim() : "";
    } catch {
      return "";
    }
  };

  /**
   * @param {string} key
   * @param {string} value
   */
  const setSession = (key, value) => {
    try {
      if (!value) {
        win.sessionStorage.removeItem(key);
        return;
      }
      win.sessionStorage.setItem(key, value);
    } catch {
      // ignore
    }
  };

  /** Persist `src` from URL when present (e.g. direct product landing). */
  const captureSrcFromUrl = () => {
    const src = getParam(URL_PARAM);
    if (src) setSession(SESSION_KEY, src);
  };

  /**
   * Prefer session; else URL `src`, then persist to session.
   * @returns {string} Empty when no session and no `src` in URL.
   */
  const resolveAdSource = () => {
    const fromSession = getSession(SESSION_KEY);
    if (fromSession) return fromSession;
    const fromUrl = getParam(URL_PARAM);
    if (fromUrl) {
      setSession(SESSION_KEY, fromUrl);
      return fromUrl;
    }
    return "";
  };

  /**
   * @param {{ item_count?: number; items?: unknown[] } | undefined} cart
   * @returns {boolean}
   */
  const cartHasLineItems = (cart) => {
    if (!cart) return false;
    if (typeof cart.item_count === "number" && cart.item_count > 0) return true;
    return Array.isArray(cart.items) && cart.items.length > 0;
  };

  /**
   * @param {{ attributes?: Record<string, unknown> } | undefined} cart
   * @returns {string}
   */
  const getCartAdSourceValue = (cart) => {
    const attrs = cart?.attributes;
    if (!attrs || typeof attrs !== "object") return "";
    const raw = ATTR_KEY in attrs ? attrs[ATTR_KEY] : "";
    return raw != null ? String(raw).trim() : "";
  };

  /**
   * @param {string} adSource
   */
  const applyCartAttribute = (adSource) => {
    try {
      void win.fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          attributes: { [ATTR_KEY]: adSource },
        }),
      });
    } catch {
      // ignore
    }
  };

  /**
   * Set _ad_source from session/url, or clear only when cart is empty and no src resolved.
   * @param {string} desired
   */
  const reconcileCartAdSource = (desired) => {
    const desiredTrim = typeof desired === "string" ? desired.trim() : "";

    void win
      .fetch("/cart.js", { credentials: "same-origin", cache: "no-store" })
      .then((r) => r.json())
      .then((cart) => {
        const current = getCartAdSourceValue(cart);

        if (desiredTrim) {
          if (current === desiredTrim) return;
          applyCartAttribute(desiredTrim);
          return;
        }

        if (cartHasLineItems(cart)) {
          return;
        }

        if (current) applyCartAttribute("");
      })
      .catch(() => {});
  };

  /**
   * @param {Event & { detail?: { data?: { didError?: boolean; source?: string } } }} event
   * @returns {boolean}
   */
  const shouldHandleCartEvent = (event) => {
    const data = event.detail?.data;
    if (data?.didError) return false;
    const source = String(data?.source || "");
    if (source && IGNORE_SOURCES[source]) return false;
    return true;
  };

  /**
   * @param {Event & { detail?: { data?: { didError?: boolean; source?: string } } }} event
   */
  const onCartUpdate = (event) => {
    if (!shouldHandleCartEvent(event)) return;
    reconcileCartAdSource(resolveAdSource());
  };

  const runPageLoadReconcile = () => {
    reconcileCartAdSource(resolveAdSource());
  };

  captureSrcFromUrl();
  document.addEventListener("cart:update", onCartUpdate);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", runPageLoadReconcile);
  } else {
    runPageLoadReconcile();
  }
})();